import { debugFlag } from '@/app/debugFlags/debugFlags';
import { events } from '@/app/events/events';
import { sheets } from '@/app/grid/controller/Sheets';
import { debugRendererLight, debugTimeCheck, debugTimeReset } from '@/app/gridGL/helpers/debugPerformance';
import { FPS } from '@/app/gridGL/helpers/Fps';
import { content } from '@/app/gridGL/pixiApp/contentInstance';
import { pixiApp } from '@/app/gridGL/pixiApp/PixiApp';
import { thumbnail } from '@/app/gridGL/pixiApp/thumbnail';

export class Update {
  private raf?: number;
  private fps?: FPS;

  /** True after the first cells-ready frame has finished `content.update` (headings have real sizes). */
  firstRenderComplete = false;

  private initialViewportLoadDone = false;

  constructor() {
    if (debugFlag('debugShowFPS')) {
      this.fps = new FPS();
    }
  }

  resetFirstRenderComplete() {
    this.firstRenderComplete = false;
  }

  start() {
    if (!this.raf) {
      this.raf = requestAnimationFrame(this.update);
    }
  }

  destroy() {
    if (this.raf) {
      cancelAnimationFrame(this.raf);
      this.raf = undefined;
    }
  }

  private lastFocusElement?: HTMLElement;
  private showFocus = () => {
    const focus = document.activeElement;
    if (focus !== this.lastFocusElement) {
      this.lastFocusElement = focus as HTMLElement;
      console.log('current focus:', focus);
    }
  };

  private updateFps() {
    if (!this.fps && debugFlag('debugShowFPS')) {
      this.fps = new FPS();
    }
    this.fps?.update();
  }

  // update loop w/debug checks
  private update = () => {
    if (pixiApp.destroyed) return;

    if (content.copying) {
      this.raf = requestAnimationFrame(this.update);
      return;
    }

    if (!content.cellsSheets.isReady()) {
      this.raf = requestAnimationFrame(this.update);
      return;
    }

    if (!this.initialViewportLoadDone) {
      this.initialViewportLoadDone = true;
      if (!sheets.sheet.cursor.persistedViewport) {
        content.headings.dirty = true;
        content.headings.update(true);
      }
      pixiApp.viewport.loadViewport();
    }

    if (debugFlag('debugShowFocus')) {
      this.showFocus();
    }

    const viewportChanged = pixiApp.viewport.updateViewport();
    if (viewportChanged && debugFlag('debugShowWhyRendering')) {
      console.log('dirty: pixiApp viewport');
    }

    const contentDirty = content.update(viewportChanged);

    if (pixiApp.viewport.dirty || contentDirty) {
      debugTimeReset();
      pixiApp.viewport.dirty = false;
      pixiApp.renderer.render(pixiApp.stage);
      debugTimeCheck('[Update] render');
      debugRendererLight(true);
      thumbnail.rendererBusy();
      events.emit('viewportReadyAfterUpdate');
    } else {
      debugRendererLight(false);
      thumbnail.check();
    }

    if (!this.firstRenderComplete) {
      this.firstRenderComplete = true;
    }

    this.raf = requestAnimationFrame(this.update);
    this.updateFps();
  };
}
